@if (isOpen()) {
  <div class="command-palette-backdrop"
       animate.enter="command-palette-backdrop-enter"
       animate.leave="command-palette-backdrop-leave"
       (click)="onBackdropClick($event)">
    <div class="command-palette"
         #palette
         animate.enter="command-palette-enter"
         animate.leave="command-palette-leave"
         role="dialog"
         aria-modal="true"
         pFocusTrap
         [attr.aria-label]="'search.ariaSearch' | translate">
      <!-- Search Input -->
      <div class="palette-header">
        <div class="search-input-container">
          <fa-duotone-icon [icon]="faMagnifyingGlass" class="search-icon" [class.searching]="isLoading()"></fa-duotone-icon>
          <div class="input-wrapper">
            @if (ghostText()) {
              <span class="ghost-text" aria-hidden="true">{{ ghostText() }}</span>
            }
            <input
              #searchInput
              type="text"
              class="palette-input"
              [placeholder]="'search.placeholder' | translate"
              [ngModel]="searchQuery()"
              (ngModelChange)="onSearchChange($event)"
              [attr.maxlength]="searchQueryMaxLength"
              autocomplete="off"
              spellcheck="false"
            />
          </div>
          <!-- The ceiling, said as the member nears it (the server refuses a longer query) — the one counter home. -->
          <app-char-counter class="char-counter-slot" [left]="charsLeft()"/>
          @if (hasQuery()) {
            <button
              type="button"
              class="clear-button"
              (click)="clearSearch()"
              [attr.aria-label]="'common.clear' | translate"
              animate.enter="palette-item-enter">
              <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
            </button>
          }
        </div>
        <div class="header-actions">
          <span class="shortcut-hint">{{ getShortcutDisplay() }}</span>
        </div>
      </div>

      <!-- Content Area -->
      <div class="palette-content">
        <!-- Loading State with Skeletons -->
        @if (isLoading()) {
          <div class="section" animate.enter="palette-item-enter">
            <div class="section-header">
              <span class="section-title skeleton-text" style="width: 100px;"></span>
            </div>
            <ul class="results-list skeleton-list">
              @for (i of [1, 2, 3, 4]; track i) {
                <li class="result-item skeleton-item">
                  <div class="result-thumbnail skeleton"></div>
                  <div class="result-content">
                    <span class="result-title skeleton-text" [style.width.%]="60 + (i * 8)"></span>
                    <span class="result-path skeleton-text" style="width: 40%;"></span>
                  </div>
                </li>
              }
            </ul>
          </div>
        }

        <!-- Recent Searches (when no query) -->
        @if (showRecentSearches() && !isLoading()) {
          <div class="section" animate.enter="palette-item-enter">
            <div class="section-header">
              <span class="section-title">
                <fa-duotone-icon [icon]="faClockRotateLeft" class="section-icon"></fa-duotone-icon>
                {{ 'search.recentSearches' | translate }}
              </span>
              <button
                type="button"
                class="clear-all-button"
                (click)="clearAllRecentSearches()">
                {{ 'common.clearAll' | translate }}
              </button>
            </div>
            <ul class="results-list" role="listbox">
              @for (query of recentSearches(); track query; let i = $index) {
                <li
                  class="result-item recent-item"
                  animate.enter="result-item-enter"
                  [style.animation-delay.ms]="i * 30"
                  [class.selected]="selectedIndex() === i"
                  role="option"
                  [attr.aria-selected]="selectedIndex() === i"
                  (click)="selectRecentSearch(query)"
                  (mouseenter)="selectedIndex.set(i)">
                  <fa-duotone-icon [icon]="faClockRotateLeft" class="item-icon"></fa-duotone-icon>
                  <span class="item-text">{{ query }}</span>
                  <button
                    type="button"
                    class="remove-button"
                    (click)="removeRecentSearch($event, query)"
                    [attr.aria-label]="'common.remove' | translate">
                    <fa-duotone-icon [icon]="faTrash"></fa-duotone-icon>
                  </button>
                  <span class="item-hint">↵</span>
                </li>
              }
            </ul>
          </div>
        }

        <!-- Search Results -->
        @if (showResults() && !isLoading()) {
          <div class="section" animate.enter="palette-item-enter">
            <div class="section-header">
              <span class="section-title">{{ 'search.topResults' | translate }}</span>
              <span class="results-count">{{ totalResults() | l10nPlural: 'search.results' }}</span>
            </div>
            <ul class="results-list" role="listbox">
              @for (result of results(); track result.id; let i = $index) {
                <li
                  class="result-item"
                  animate.enter="result-item-enter"
                  [style.animation-delay.ms]="i * 30"
                  [class.selected]="selectedIndex() === i"
                  role="option"
                  [attr.aria-selected]="selectedIndex() === i"
                  (click)="selectResult(result)"
                  (mouseenter)="selectedIndex.set(i)">
                  <!-- Thumbnail or Icon -->
                  <div class="result-thumbnail">
                    @if (result.thumbnail) {
                      <img [src]="result.thumbnail" [alt]="result.displayTitle" loading="lazy" />
                    } @else {
                      <fa-duotone-icon [icon]="getResultIcon(result)" class="result-type-icon"></fa-duotone-icon>
                    }
                  </div>
                  <!-- Content -->
                  <div class="result-content">
                    <span class="result-title">
                      @if (isUntitledAnnouncement(result)) {
                        {{ 'search.types.untitledAnnouncement' | translate }}
                      } @else {
                        {{ result.displayTitle }}
                      }
                      <!-- The result is shown in another language than the member's — the same
                           substitution marker the result card carries; without it a language
                           swap (the announcement locale fallback) would be invisible here. -->
                      @if (result.isPreferredLocale === false && result.locale) {
                        <span class="result-locale-marker">{{ result.locale }}</span>
                      }
                    </span>
                    @if (getModuleLabelKey(result); as moduleKey) {
                      <span class="result-path">{{ moduleKey | translate }}</span>
                    }
                  </div>
                  <!-- Hint -->
                  <span class="item-hint">↵</span>
                </li>
              }
            </ul>
          </div>
        }

        <!-- Query refused (over-long): the server's sentence in place of "no results" -->
        @if (queryRefusal()) {
          <div class="empty-state" animate.enter="palette-item-enter">
            <fa-duotone-icon [icon]="faMagnifyingGlass" class="empty-icon"></fa-duotone-icon>
            <span class="empty-description">{{ queryRefusal() }}</span>
          </div>
        }

        <!-- Empty State (no results found) -->
        @if (showEmptyState()) {
          <div class="empty-state" animate.enter="palette-item-enter">
            <fa-duotone-icon [icon]="faMagnifyingGlass" class="empty-icon"></fa-duotone-icon>
            <span class="empty-title">{{ 'search.noResults' | translate }}</span>
            <span class="empty-description">{{ 'search.noResultsDescription' | translate }}</span>
          </div>
        }

        <!-- Suggestions (when no query and no recent) -->
        @if (!hasQuery() && !hasRecentSearches() && !isLoading()) {
          <div class="suggestions" animate.enter="palette-item-enter">
            <span class="suggestions-label">{{ 'search.trySearching' | translate }}</span>
            <!-- Each chip lists ONE type: the member sees their language's word, the request
                 carries the type filter — the same listing in every language. -->
            <div class="suggestion-chips">
              @for (chip of suggestionChips; track chip.labelKey) {
                <button type="button" class="suggestion-chip" (click)="searchSuggestion(chip)">
                  {{ chip.labelKey | translate }}
                </button>
              }
            </div>
          </div>
        }
      </div>

      <!-- Footer -->
      @if (hasQuery() && (hasResults() || isLoading())) {
        <div class="palette-footer" animate.enter="palette-item-enter">
          <div class="footer-hints">
            <span class="hint"><kbd>↑</kbd><kbd>↓</kbd> {{ 'search.hints.navigate' | translate }}</span>
            <span class="hint"><kbd>↵</kbd> {{ 'search.hints.open' | translate }}</span>
            <span class="hint"><kbd>esc</kbd> {{ 'search.hints.close' | translate }}</span>
          </div>
          <button
            type="button"
            class="see-all-button"
            (click)="goToFullResults()"
            [disabled]="isLoading()">
            <span>{{ 'search.seeAllResults' | translate }}</span>
            <fa-duotone-icon [icon]="faArrowRight"></fa-duotone-icon>
          </button>
        </div>
      }
    </div>
  </div>
}
